import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Mail, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const ReferralConfirmation = ({ formData, submissionId }) => {
  const emailReferrals = (formData.referrals || []).filter(r => r.sendEmail);
  const manualReferrals = (formData.referrals || []).filter(r => !r.sendEmail);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Thank You for Your Referral | KGOB CPAs</title>
      </Helmet>
      
      <Header />
      
      <main className="py-12 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#0B2240] mb-4">
              Thank You for Your Referral{formData.referrals.length > 1 ? 's' : ''}!
            </h1>
            <p className="text-lg text-[#4C5A70]">
              We've received your {formData.referrals.length} referral{formData.referrals.length > 1 ? 's' : ''} and will handle them with care
            </p>
          </motion.div>

          <Card className="p-8 border-2 border-[#E1DFD7] mb-6">
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-6">
              What Happens Next:
            </h2>
            
            {emailReferrals.length > 0 && (
              <div className="mb-6">
                <div className="flex gap-3 mb-3">
                  <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0B2240] mb-2">Email Introductions ({emailReferrals.length})</p>
                    {(emailReferrals || []).map((ref, i) => (
                      <p key={i} className="text-sm text-[#4C5A70] mb-1">
                        ✓ {ref.firstName} {ref.lastName} will receive an introduction email within 5 minutes
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {manualReferrals.length > 0 && (
              <div className="mb-6">
                <div className="flex gap-3 mb-3">
                  <Clock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0B2240] mb-2">Manual Follow-Up ({manualReferrals.length})</p>
                    {(manualReferrals || []).map((ref, i) => (
                      <p key={i} className="text-sm text-[#4C5A70] mb-1">
                        ✓ {ref.firstName} {ref.lastName} will be contacted within 2 business days
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="bg-[#F7F6F2] p-4 rounded-lg border-l-4 border-[#D4AF37]">
              <p className="text-sm text-[#4C5A70]">
                <strong className="text-[#0B2240]">You'll receive:</strong> A confirmation email with all referral details
              </p>
            </div>
          </Card>

          <div className="bg-[#F7F6F2] p-6 rounded-lg border-l-4 border-[#D4AF37] mb-8">
            <p className="text-sm text-[#4C5A70]">
              <strong className="text-[#0B2240]">Questions?</strong> Email us at{' '}
              <a data-testid="referral-confirmation-a-1" href="mailto:referrals@kgob.com" className="text-[#D4AF37] hover:underline font-medium">
                referrals@kgob.com
              </a>{' '}
              or call (704) 599-3355
            </p>
          </div>

          <div className="text-center space-x-4">
            <Button data-testid="referral-confirmation-button-1" asChild className="h-12 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
              <Link data-testid="referral-confirmation-link-1" to="/refer-us">Refer Someone Else</Link>
            </Button>
            <Button data-testid="referral-confirmation-button-1" asChild variant="outline" className="h-12 px-8 border-2 border-[#0B2240] text-[#0B2240]">
              <Link data-testid="referral-confirmation-link-1" to="/">Return to Home</Link>
            </Button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};